import { useAuthReady } from './useAuthReady';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { moneroBalanceKey } from './useMoneroBalance';
import type { AnyTokenSymbol, Chain } from '@incpt/shared';

export interface WalletAddress {
  id: string;
  /** The shared chain union, not a copy. Written out here as
   *  'eth' | 'trx' | 'btc', this silently mistyped every row for every chain
   *  added since — and no compile error was possible, because it describes a
   *  response the server parses, not a value this file constructs. */
  chain: Chain;
  eoaAddress: string;
  smartAccountAddress: string | null;
  publicKey: string;
  derivationIndex: number;
  label: string | null;
  /** When the row was registered. Only Monero reads it, and it is load
   *  bearing there: scanning starts from this point, because an address
   *  cannot have received anything before it existed. */
  createdAt?: string;
}

export interface Wallet {
  id: string;
  name: string;
  seedBackupMethod: string;
  addresses: WalletAddress[];
}

export interface Balance {
  chain: Chain;
  token: AnyTokenSymbol;
  address: string;
  raw: string;
  /** BTC only: signed mempool delta (negative while own change is unconfirmed). */
  unconfirmedRaw?: string;
  /**
   * The server could not compute this amount and never can — the device has
   * to. True only for Monero. The row still carries the price and decimals so
   * the coin renders; `raw` is zero until a scan fills it in, which is what
   * the merge below does.
   */
  deviceOnly?: boolean;
  /**
   * RPC read failed on the server. `raw` is a placeholder — keep the last
   * good amount for this address×token instead of wiping the wallet to 0.
   */
  unavailable?: boolean;
  decimals: number;
  /** Live spot for natives; 1 for stables; null while the price feed is cold. */
  priceUsd?: number | null;
}

const balanceKey = (b: Pick<Balance, 'chain' | 'token' | 'address'>) =>
  `${b.chain}:${b.token}:${b.address}`;

/** Prefer last-known amounts when the server marks a row `unavailable`. */
export function mergeBalances(prev: Balance[] | undefined, next: Balance[]): Balance[] {
  if (!prev?.length) return next;
  const prior = new Map(prev.map((b) => [balanceKey(b), b]));
  return next.map((b) => {
    if (!b.unavailable) return b;
    const old = prior.get(balanceKey(b));
    if (!old) return b;
    return {
      ...b,
      raw: old.raw,
      unconfirmedRaw: old.unconfirmedRaw ?? b.unconfirmedRaw,
      // Keep showing the last good figure; still flag so UI can hint if needed.
      unavailable: true,
    };
  });
}

const toAmount = (b: Balance) => Number(b.raw) / 10 ** b.decimals;
/** USD value of a balance row — natives multiply by their live price. A cold
 *  price (null) contributes 0 to totals rather than a wildly wrong "≈$1". */
export function balanceUsd(b: Balance): number {
  return toAmount(b) * (b.priceUsd ?? (b.token === 'USDT' || b.token === 'USDC' ? 1 : 0));
}

/**
 * The user's wallet + live balances. Stablecoins are ~$1; native ETH/BTC carry
 * a per-row `priceUsd` from the backend. Shared via the query cache.
 */
export function useWallet() {
  const { canFetch: ready, initData, cacheUserId: userId } = useAuthReady();

  const walletQ = useQuery({
    queryKey: ['wallet', userId],
    enabled: ready,
    queryFn: () => api<{ wallet: Wallet | null }>(initData, '/v1/wallets'),
    refetchOnMount: 'always',
  });
  const wallet = walletQ.data?.wallet ?? null;

  const qc = useQueryClient();

  const balancesQ = useQuery({
    queryKey: ['balances', wallet?.id, userId],
    enabled: ready && !!wallet?.id,
    refetchInterval: 30_000,
    refetchOnMount: 'always',
    queryFn: async () => {
      const data = await api<{ balances: Balance[] }>(
        initData,
        `/v1/wallets/${wallet!.id}/balances`,
      );
      const prev = qc.getQueryData<{ balances: Balance[] }>(['balances', wallet!.id, userId]);
      return { balances: mergeBalances(prev?.balances, data.balances) };
    },
  });

  const serverBalances = balancesQ.data?.balances ?? [];

  // Fold device-scanned Monero into the shared list so every screen reads one
  // number. The scan is driven by whichever screen asked for it.
  const balances = serverBalances.map((b) => {
    if (!b.deviceOnly) return b;
    const scanned = qc.getQueryData<string>(moneroBalanceKey(b.address));
    return scanned ? { ...b, raw: scanned } : b;
  });
  const totalUsd = balances.reduce((sum, b) => sum + balanceUsd(b), 0);

  /** Per-token totals across chains (TOKEN AMOUNTS, not USD). */
  const byToken = balances.reduce<Record<string, number>>((acc, b) => {
    acc[b.token] = (acc[b.token] ?? 0) + toAmount(b);
    return acc;
  }, {});

  /** Per token × chain totals (token amounts), keyed "USDT:eth" / "BTC:btc" / … */
  const byTokenChain = balances.reduce<Record<string, number>>((acc, b) => {
    const k = `${b.token}:${b.chain}`;
    acc[k] = (acc[k] ?? 0) + toAmount(b);
    return acc;
  }, {});

  /** Per token × chain USD totals, same keys as byTokenChain. */
  const byTokenChainUsd = balances.reduce<Record<string, number>>((acc, b) => {
    const k = `${b.token}:${b.chain}`;
    acc[k] = (acc[k] ?? 0) + balanceUsd(b);
    return acc;
  }, {});

  /** Live USD price per token symbol (1 for stables; undefined while cold). */
  const priceBySymbol = balances.reduce<Record<string, number>>((acc, b) => {
    if (b.priceUsd != null) acc[b.token] = b.priceUsd;
    else if (b.token === 'USDT' || b.token === 'USDC') acc[b.token] = 1;
    return acc;
  }, {});

  return {
    wallet,
    balances,
    byToken,
    byTokenChain,
    byTokenChainUsd,
    priceBySymbol,
    totalUsd,
    loading: walletQ.isLoading || balancesQ.isLoading,
  };
}
